import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './lib/auth-context';
import { ErrorBoundary } from './components/ErrorBoundary';

// Eagerly load the landing page — it's the entry point for unauthenticated users
import LandingPage from './pages/landing';

// Auth pages — lightweight, load fast
const LoginPage = lazy(() => import('./pages/auth/login'));
const SignupPage = lazy(() => import('./pages/auth/signup'));
const ForgotPasswordPage = lazy(() => import('./pages/auth/forgot-password'));
const ResetPasswordPage = lazy(() => import('./pages/auth/reset-password'));
const VerifyEmailPage = lazy(() => import('./pages/auth/verify-email'));

// Onboarding — only loaded after signup
const OnboardingLayout = lazy(() => import('./pages/onboarding/layout'));
const OnboardingAudiencePage = lazy(() => import('./pages/onboarding/audience'));
const OnboardingTonePage = lazy(() => import('./pages/onboarding/tone'));
const OnboardingSourcesPage = lazy(() => import('./pages/onboarding/sources'));
const OnboardingSubscribersPage = lazy(() => import('./pages/onboarding/subscribers'));
const OnboardingSignaturePage = lazy(() => import('./pages/onboarding/signature'));
const OnboardingSummaryPage = lazy(() => import('./pages/onboarding/summary'));
const OnboardingPlanPage = lazy(() => import('./pages/onboarding/plan'));

// Dashboard — heaviest chunk, only loaded after auth + onboarding
const DashboardLayout = lazy(() => import('./pages/dashboard/layout'));
const ArticlesPage = lazy(() => import('./pages/dashboard/articles'));
const NewslettersPage = lazy(() => import('./pages/dashboard/newsletters'));
const NewsletterDetailPage = lazy(() => import('./pages/dashboard/newsletter-detail'));
const NewsletterLayoutPage = lazy(() => import('./pages/dashboard/newsletter-layout'));
const SourcesPage = lazy(() => import('./pages/dashboard/sources'));
const SourceDiscoveryPage = lazy(() => import('./pages/dashboard/source-discovery'));
const AudiencesPage = lazy(() => import('./pages/dashboard/audiences'));
const AudienceDetailPage = lazy(() => import('./pages/dashboard/audience-detail'));
const SettingsPage = lazy(() => import('./pages/dashboard/settings'));

// Help
const HelpLayout = lazy(() => import('./pages/help/layout'));
const GettingStartedPage = lazy(() => import('./pages/help/getting-started'));
const FaqPage = lazy(() => import('./pages/help/faq'));

function PageLoader() {
  return (
    <div className="flex h-screen items-center justify-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
    </div>
  );
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, user, loading } = useAuth();
  const location = useLocation();

  if (loading) return <PageLoader />;

  if (!isAuthenticated) {
    return <Navigate to="/welcome" replace />;
  }

  // Redirect to onboarding if not completed (but don't redirect if already on onboarding)
  const isOnboardingRoute = location.pathname.startsWith('/onboarding');
  if (user && !user.hasCompletedOnboarding && !isOnboardingRoute) {
    return <Navigate to="/onboarding/audience" replace />;
  }

  // If subscription is canceled or past_due, redirect to plan selection to resubscribe
  // (but don't redirect if already on onboarding/plan or settings where they can manage billing)
  const subStatus = user?.organization?.subscription?.status;
  const isSettingsRoute = location.pathname === '/settings';
  if (user && user.hasCompletedOnboarding && !isOnboardingRoute && !isSettingsRoute) {
    if (subStatus === 'CANCELED' || subStatus === 'PAST_DUE') {
      return <Navigate to="/onboarding/plan" replace />;
    }
  }

  return <>{children}</>;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, user, loading } = useAuth();

  if (loading) return <PageLoader />;

  if (isAuthenticated) {
    if (user && !user.hasCompletedOnboarding) {
      return <Navigate to="/onboarding/audience" replace />;
    }
    return <Navigate to="/articles" replace />;
  }

  return <>{children}</>;
}

function RootRoute() {
  const { isAuthenticated, user, loading } = useAuth();

  if (loading) return <PageLoader />;

  if (isAuthenticated) {
    if (user && !user.hasCompletedOnboarding) {
      return <Navigate to="/onboarding/audience" replace />;
    }
    return <Navigate to="/articles" replace />;
  }

  return <LandingPage />;
}

export default function App() {
  return (
    <ErrorBoundary>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Root: landing page for unauth, redirect to dashboard for auth */}
          <Route path="/" element={<RootRoute />} />

          {/* /welcome — always show landing page; PublicRoute redirects if already logged in */}
          <Route path="/welcome" element={<PublicRoute><LandingPage /></PublicRoute>} />

          {/* Public auth routes */}
          <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
          <Route path="/signup" element={<PublicRoute><SignupPage /></PublicRoute>} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />

          {/* Onboarding routes */}
          <Route path="/onboarding" element={<ProtectedRoute><OnboardingLayout /></ProtectedRoute>}>
            <Route path="audience" element={<OnboardingAudiencePage />} />
            <Route path="tone" element={<OnboardingTonePage />} />
            <Route path="sources" element={<OnboardingSourcesPage />} />
            <Route path="subscribers" element={<OnboardingSubscribersPage />} />
            <Route path="signature" element={<OnboardingSignaturePage />} />
            <Route path="summary" element={<OnboardingSummaryPage />} />
            <Route path="plan" element={<OnboardingPlanPage />} />
          </Route>

          {/* Dashboard routes */}
          <Route element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
            <Route path="articles" element={<ArticlesPage />} />
            <Route path="newsletters" element={<NewslettersPage />} />
            <Route path="newsletters/:id" element={<NewsletterDetailPage />} />
            <Route path="newsletters/:id/customize-layout" element={<NewsletterLayoutPage />} />
            <Route path="sources" element={<SourcesPage />} />
            <Route path="sources/discover" element={<SourceDiscoveryPage />} />
            <Route path="audiences" element={<AudiencesPage />} />
            <Route path="audiences/:id" element={<AudienceDetailPage />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="help" element={<HelpLayout />}>
              <Route path="getting-started" element={<GettingStartedPage />} />
              <Route path="faq" element={<FaqPage />} />
            </Route>
          </Route>

          {/* Catch all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
}
